/* R796 Battlegrounds page: the hash router (contract §7.4), in the R800 Battlegrounds 2 menus
   (docs/BATTLEGROUNDS-2-DESIGN.md §10): the Hub's Home and three tabs.
   Tabs in order Home | Hero | Party | Shop, Home by default (Jan, 2026-09-26: the packs and the
   shop are one place, the packs first; 2026-09-27: "the home + play screens should be combined",
   so Home holds the chapters and Play is gone as a tab).
   Routes: #/home #/hero #/party #/shop, #/level/<n> (a chapter or level, under Home),
   #/heroes/<card_key> (a card, under Party) and #/admin (only when the last home reply said
   admin). The older routes still open their new place, carrying route.alias: #/battle, #/events,
   #/play, #/levels and #/map go Home (the last three open its campaign sheet once), #/heroes and
   #/upgrades to Party, #/packs to the Shop (which then shows its packs), and a saved older tab
   maps the same way.
   2026-10-05 (the menus redesign, docs/battlegrounds/menus-2026-10-05): the Hero tab reads "Champion" (every card is a
   hero, so the old name was unclear). Its id stays 'hero', so #/hero and the older #/champion both open it.
   A tab change uses history.replaceState, so tabs never pile up in Back; a deeper view (a card,
   a level, admin) is pushed so Back returns to its tab. Every route change is written to the
   store and focus moves to the panel's heading with preventScroll. It never scrolls, smoothly
   or otherwise. A navigation the browser makes (Back, Forward, a typed hash) first closes any
   open sheet, so a sheet never stays up over a screen it does not belong to. */

import { closeSheets } from 'bg-ui';

export const TABS = Object.freeze([
  Object.freeze({ id: 'home', label: 'Home' }),
  Object.freeze({ id: 'hero', label: 'Champion' }),
  Object.freeze({ id: 'party', label: 'Party' }),
  Object.freeze({ id: 'shop', label: 'Shop' })
]);
export const DEFAULT_TAB = 'home';
const TAB_SET = new Set(TABS.map(t => t.id));
/* the older names, kept working (bookmarks, the Hall's links, a saved tab) */
const ALIAS = Object.freeze({ battle: 'home', events: 'home', play: 'home', levels: 'home', map: 'home', heroes: 'party', upgrades: 'party', champion: 'hero', packs: 'shop' });
export function tabOf(id) { return TAB_SET.has(id) ? id : (ALIAS[id] || null); }
const CARD_KEY_RE = /^(own|ff):[0-9a-f-]{36}$/;

/* Pure: a hash to a route. Unknown or forbidden routes fall back to a tab. */
export function parseRoute(hash, options) {
  const admin = !!(options && options.admin);
  const fallbackTab = tabOf(options && options.fallbackTab) || DEFAULT_TAB;
  const raw = String(hash || '').replace(/^#/, '');
  const parts = raw.replace(/^\/+/, '').split('/').filter(Boolean);
  const tabRoute = tab => ({ tab: tab, view: tab, path: '#/' + tab, params: {}, deep: false });
  if (!parts.length) return Object.assign(tabRoute(fallbackTab), { normalized: true });
  const head = parts[0];
  if (TAB_SET.has(head) && parts.length === 1) return tabRoute(head);
  if (head === 'heroes' && parts.length === 2) {
    let key = '';
    try { key = decodeURIComponent(parts[1]).toLowerCase(); } catch (_) { key = ''; }
    if (CARD_KEY_RE.test(key)) return { tab: 'party', view: 'card', path: '#/heroes/' + key, params: { card_key: key }, deep: true };
    return Object.assign(tabRoute('party'), { normalized: true });
  }
  if (head === 'level' && parts.length === 2 && /^[0-9]{1,3}$/.test(parts[1])) {
    const n = parseInt(parts[1], 10);
    if (n >= 1 && n <= 100) return { tab: 'home', view: 'level', path: '#/level/' + n, params: { level: n }, deep: true };
    return Object.assign(tabRoute('home'), { normalized: true });
  }
  if (head === 'admin' && parts.length === 1) {
    if (admin) return { tab: null, view: 'admin', path: '#/admin', params: {}, deep: true };
    return Object.assign(tabRoute(DEFAULT_TAB), { normalized: true });
  }
  if (ALIAS[head] && parts.length === 1) return Object.assign(tabRoute(ALIAS[head]), { normalized: true, alias: head });
  return Object.assign(tabRoute(fallbackTab), { normalized: true });
}

function focusHeading(root, view) {
  if (!root) return null;
  const panel = root.querySelector('[data-bg-panel="' + view + '"]');
  const heading = panel && panel.querySelector('[data-bg-heading]');
  if (!heading) return null;
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  try { heading.focus({ preventScroll: true }); } catch (_) { try { heading.focus(); } catch (__) {} }
  return heading;
}

/* createRouter({store, root, onRoute}). start() reads the current hash (or the
   saved tab when there is none), settles it and emits it. go(path) and
   tab(id) change it. */
export function createRouter(opts) {
  const store = opts && opts.store;
  const root = opts && opts.root;
  const onRoute = opts && typeof opts.onRoute === 'function' ? opts.onRoute : null;
  let started = false;

  function isAdmin() {
    const home = store && store.get().home;
    return !!(home && home.admin === true);
  }
  function savedTab() {
    const ui = store && store.get().ui;
    return (ui && tabOf(ui.tab)) || DEFAULT_TAB;
  }
  function emit(route, focus) {
    if (store) {
      const s = store.get();
      const patch = { route: route };
      if (route.tab && s.ui && s.ui.tab !== route.tab) patch.ui = Object.assign({}, s.ui, { tab: route.tab });
      store.set(patch);
    }
    if (onRoute) onRoute(route);
    if (focus !== false) focusHeading(root, route.view);
    return route;
  }
  /* a pushed view remembers the path it was opened from (history.state.bgFrom) */
  function write(path, push, from) {
    try {
      if (location.hash === path) return;
      if (push) history.pushState(from ? { bgFrom: from } : null, '', path);
      else history.replaceState(null, '', path);
    } catch (_) {}
  }
  function resolve(focus) {
    const route = parseRoute(location.hash, { admin: isAdmin(), fallbackTab: savedTab() });
    if (route.path !== location.hash) write(route.path, false);
    return emit(route, focus);
  }
  /* 2026-09-28: a tab tapped from a pushed view (a hero, a chapter's page) replaced that entry with the tab's path, so
     the tab stood in Back twice and the next Back did nothing. When the tab is the very place the view was opened
     from, the router steps Back to it instead. A tap while that step is under way waits for it, then goes. */
  let backing = null;
  function go(path, options) {
    const route = parseRoute(path, { admin: isAdmin(), fallbackTab: savedTab() });
    if (backing) { backing.then = { path: path, options: options }; return route; }
    const cur = store && store.get().route;
    let st = null;
    try { st = history.state; } catch (_) {}
    if (!route.deep && cur && cur.deep && !(options && options.replace) && st && st.bgFrom === route.path && location.hash === cur.path) {
      const b = backing = { to: route.path, then: null, timer: 0 };
      /* no answer from the browser (none should be missing): carry on as a plain tab change */
      b.timer = setTimeout(() => { if (backing === b) { backing = null; write(route.path, false); emit(route, options && options.focus); after(b); } }, 600);
      try { history.back(); return route; } catch (_) { clearTimeout(b.timer); backing = null; }
    }
    const push = route.deep && !(options && options.replace) && !(cur && cur.path === route.path);
    write(route.path, push, cur && cur.path);
    return emit(route, options && options.focus);
  }
  function after(b) { if (b && b.then) go(b.then.path, b.then.options); }
  /* Back from a pushed view fires popstate and hashchange together: answer once */
  function onHash() {
    const b = backing;
    if (b) { clearTimeout(b.timer); backing = null; }
    const cur = store && store.get().route;
    if (cur && cur.path === location.hash) { after(b); return; }
    try { closeSheets(); } catch (_) {}
    resolve(true);
    after(b);
  }

  return Object.freeze({
    start() {
      if (started) return resolve(false);
      started = true;
      window.addEventListener('hashchange', onHash);
      window.addEventListener('popstate', onHash);
      return resolve(false);
    },
    stop() {
      started = false;
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    },
    go: go,
    tab(id) { return go('#/' + (tabOf(id) || DEFAULT_TAB)); },
    resolve: resolve,
    current: () => (store ? store.get().route : parseRoute(location.hash, {}))
  });
}
